import React, { useState, useRef, useEffect } from 'react';

function getTier(score) {
  if (score >= 900) return 'EXCELLENT';
  if (score >= 800) return 'VERY GOOD';
  if (score >= 670) return 'GOOD';
  if (score >= 500) return 'FAIR';
  return 'POOR';
}

// Catmull-Rom → cubic bezier smooth path
function smoothPath(pts) {
  if (pts.length < 2) return '';
  const d = [`M ${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}`];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d.push(`C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`);
  }
  return d.join(' ');
}

const PAD_L = 44;
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 28;
const CHART_H = 149;
const SVG_H = PAD_T + CHART_H + PAD_B; // 189

const ScoreHistoryChart = ({ data = [] }) => {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      setWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const CW = Math.max(0, width - PAD_L - PAD_R);

  // Fixed score domain so the Y-axis always spans the full 0–1000 range.
  const minV = 0;
  const maxV = 1000;
  const range = maxV - minV;

  const yFor = v => PAD_T + CHART_H - ((v - minV) / range) * CHART_H;
  const xFor = i => PAD_L + (data.length > 1 ? (i / (data.length - 1)) * CW : CW / 2);

  const pts = data.map((d, i) => ({ x: xFor(i), y: yFor(d.v), ...d }));
  const linePath = width > 0 ? smoothPath(pts) : '';
  const areaPath =
    width > 0 && pts.length > 1
      ? `${linePath} L ${pts[pts.length - 1].x.toFixed(2)},${(PAD_T + CHART_H).toFixed(2)} L ${pts[0].x.toFixed(2)},${(PAD_T + CHART_H).toFixed(2)} Z`
      : '';

  // Only show the domain endpoints on the Y-axis.
  const yLabels = [minV, maxV];

  const handleMouseMove = e => {
    if (width === 0 || pts.length === 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    let nearest = null;
    let minDist = Infinity;
    pts.forEach(p => {
      const dist = Math.abs(p.x - mx);
      if (dist < minDist) {
        minDist = dist;
        nearest = p;
      }
    });
    if (nearest) setHover(nearest);
  };

  if (data.length === 0) {
    return (
      <div ref={containerRef} className="score-history-chart__empty">
        No history for this period
      </div>
    );
  }

  const tooltipW = 115;

  return (
    <div
      ref={containerRef}
      className="score-history-chart"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHover(null)}
    >
      {width > 0 && (
        <svg width={width} height={SVG_H} style={{ display: 'block' }}>
          <defs>
            <linearGradient id="shcAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#5CC4BB" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#5CC4BB" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Horizontal reference lines at Y-axis label values */}
          {yLabels.map(v => (
            <line
              key={v}
              x1={PAD_L}
              y1={yFor(v)}
              x2={width - PAD_R}
              y2={yFor(v)}
              stroke="#E5E7EB"
              strokeWidth="1"
            />
          ))}

          {/* Area fill */}
          {areaPath && <path d={areaPath} fill="url(#shcAreaGrad)" />}

          {/* Line */}
          {linePath && (
            <path
              d={linePath}
              fill="none"
              stroke="#5CC4BB"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {/* Y-axis labels */}
          {yLabels.map(v => (
            <text
              key={v}
              x={PAD_L - 6}
              y={yFor(v) + 3.5}
              textAnchor="end"
              fontSize="9"
              fontWeight="700"
              fill="#9CA3AF"
              fontFamily="monospace"
            >
              {v}
            </text>
          ))}

          {/* X-axis month labels */}
          {pts.map((p, i) => (
            <text
              key={i}
              x={p.x}
              y={SVG_H - 6}
              textAnchor="middle"
              fontSize="9"
              fontWeight="700"
              fill="#9CA3AF"
              fontFamily="monospace"
            >
              {data[i].m}
            </text>
          ))}

          {/* Hover dot */}
          {hover && (
            <circle cx={hover.x} cy={hover.y} r="5" fill="#5CC4BB" stroke="#fff" strokeWidth="2.5" />
          )}
        </svg>
      )}

      {/* Tooltip — flips below the dot when there isn't enough room above */}
      {hover && (
        <div
          className="score-history-chart__tooltip"
          style={{
            left: Math.min(Math.max(hover.x - tooltipW / 2, 0), width - tooltipW),
            top: hover.y - 78 >= 4 ? hover.y - 78 : hover.y + 14,
          }}
        >
          <div className="score-history-chart__tooltip-date">{hover.date}</div>
          <div className="score-history-chart__tooltip-row">
            <span className="score-history-chart__tooltip-dot" />
            <span className="score-history-chart__tooltip-score">{hover.v}</span>
            <span className="score-history-chart__tooltip-tier">{getTier(hover.v)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScoreHistoryChart;
